import type { ReactNode } from 'react';
import { useId } from 'react';

import { cn } from '@/lib/cn';

export interface TabItem<T extends string> {
  value: T;
  label: ReactNode;
  /** A small figure after the label - a pending count, a task count. */
  count?: number;
  disabled?: boolean;
}

export interface TabsProps<T extends string> {
  items: readonly TabItem<T>[];
  value: T;
  onChange: (value: T) => void;
  'aria-label': string;
  /**
   * Share this with `<TabPanel>` so the two agree on ids. Omit it when the
   * panels are not rendered as ARIA tabpanels.
   */
  idPrefix?: string;
  className?: string;
}

/**
 * A controlled tab strip. The owner holds the selected value, usually in the
 * URL, so a tab survives a reload and can be linked to.
 *
 * Arrow-key navigation follows the WAI-ARIA tabs pattern: only the selected
 * tab is in the tab order, and arrows move between them.
 */
export function Tabs<T extends string>({
  items,
  value,
  onChange,
  'aria-label': ariaLabel,
  idPrefix,
  className,
}: TabsProps<T>) {
  const generated = useId();
  const id = idPrefix ?? generated;

  const move = (direction: 1 | -1) => {
    const enabled = items.filter((item) => !item.disabled);
    const index = enabled.findIndex((item) => item.value === value);
    const next = enabled[(index + direction + enabled.length) % enabled.length];
    if (next) onChange(next.value);
  };

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn('scroll-x flex items-center gap-1 border-b border-line', className)}
      onKeyDown={(event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          move(1);
        }
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          move(-1);
        }
      }}
    >
      {items.map((item) => {
        const selected = item.value === value;
        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            id={`${id}-${item.value}`}
            aria-selected={selected}
            aria-controls={`${id}-${item.value}-panel`}
            tabIndex={selected ? 0 : -1}
            disabled={item.disabled}
            onClick={() => onChange(item.value)}
            className={cn(
              'relative -mb-px whitespace-nowrap rounded-t-sm px-3 py-2 text-ui font-medium transition-colors',
              'disabled:cursor-not-allowed disabled:opacity-40',
              selected ? 'text-ink' : 'text-muted hover:text-ink',
            )}
          >
            {item.label}
            {item.count === undefined ? null : (
              <span
                className={cn(
                  'ml-1.5 rounded-full px-1.5 py-0.5 font-mono text-nano tnum',
                  selected ? 'bg-accent-soft text-accent-ink' : 'bg-sunken text-subtle',
                )}
              >
                {item.count}
              </span>
            )}
            {selected ? (
              <span
                className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-accent"
                aria-hidden="true"
              />
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

/** The panel a tab controls. Ids must match the strip's `aria-label` owner. */
export function TabPanel({
  id,
  value,
  selected,
  children,
}: {
  /** The same `useId` value the strip generated. Pass it down from the page. */
  id: string;
  value: string;
  selected: boolean;
  children: ReactNode;
}) {
  if (!selected) return null;
  return (
    <div role="tabpanel" id={`${id}-${value}-panel`} aria-labelledby={`${id}-${value}`}>
      {children}
    </div>
  );
}
